"use client";

import { cn } from "@/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Button as AriaButton, composeRenderProps, type ButtonProps as AriaButtonProps } from "react-aria-components";

const hoverEffect = "active:scale-95";
const buttonVariants = cva(
	[
		"inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors",
		/* Disabled */
		"data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ",
		/* Focus Visible - using common focus utility */
		"focus-modern",
		/* Cross-browser fixes */
		"touch-manipulation touch-action-manipulation select-none cursor-pointer",
		/* Chromium-specific fixes */
		"[&]:active:transform [&]:active:scale-95",
		/* Ensure proper stacking */
		"relative z-auto",
		/* Transition */
		"transition-all duration-300 ease-out",
	],
	{
		variants: {
			variant: {
				default:
					"btn-gradient-primary text-primary-foreground relative overflow-hidden border border-primary/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				accent:
					"btn-gradient-accent text-white relative overflow-hidden border border-accent/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				destructive:
					"btn-gradient-destructive !text-white relative overflow-hidden border border-destructive/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				success:
					"btn-gradient-success text-success-foreground relative overflow-hidden border border-success/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				warning:
					"btn-gradient-warning text-warning-foreground relative overflow-hidden border border-warning/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				error:
					"btn-gradient-error !text-white relative overflow-hidden border border-error/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,

				outline:
					"btn-outline-shine border border-primary/20 bg-transparent text-primary data-[hovered]:bg-primary data-[hovered]:text-primary-foreground cursor-pointer transform-gpu " +
					hoverEffect,
				secondary:
					"btn-gradient-secondary text-white relative overflow-hidden border border-secondary/20 cursor-pointer shadow-lg transform-gpu " +
					hoverEffect,
				ghost: "btn-ghost-shine data-[hovered]:bg-neutral-200 data-[hovered]:text-neutral-800",
				link: "text-primary relative after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:bg-primary after:rounded-full after:transition-all after:duration-300 after:w-0 after:opacity-50 data-[hovered]:after:w-full data-[hovered]:after:left-0",
				// Mood board specific variants
				light: "bg-primary-200 text-primary-600 data-[hovered]:bg-primary-300",
				blocked:
					"bg-background border border-primary text-primary-700 data-[hovered]:bg-primary/5 relative overflow-hidden",
				bordered:
					"border-4 border-primary-300 bg-background data-[hovered]:bg-accent data-[hovered]:text-accent-foreground",
				bareshell: "",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "size-10",
				xs: "h-8 px-2 text-xs",
			},
		},
		defaultVariants: { variant: "default", size: "default" },
	},
);

interface ButtonProps extends AriaButtonProps, VariantProps<typeof buttonVariants> {
	"aria-busy"?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, ...props }, ref) => {
	return (
		<AriaButton
			ref={ref}
			className={composeRenderProps(className, customClassName =>
				cn(buttonVariants({ variant, size }), customClassName),
			)}
			style={{
				WebkitTouchCallout: "none",
				WebkitUserSelect: "none",
				...props.style,
			}}
			{...props}
		/>
	);
});
Button.displayName = "Button";

export { Button, buttonVariants };
